import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../contexts/AuthContext';
import { exportToExcel } from '../../helpers/exportCsv';
import {
  ACAO, ORIGEM, Carregando, Faixa90, Gravidade, PillOrigem, QuemResolve, dataHora, diaIso, quandoTexto, useOrdenacao,
} from './comum';
import { BarrasDia, GradeDiaHora } from './graficos';
import { Diagnostico } from './Consultas';
import { ModalDeadlock } from './Deadlocks';

const GRAV_ORDEM = { alta: 3, media: 2, baixa: 1 };
const TIPO = { deadlock: 'Deadlocks', plano_instavel: 'Às vezes muito lenta', cpu: 'Consome CPU', config: 'Configuração' };

// ── Lista completa ──────────────────────────────────────────────────────────
export function ListaProblemas({ problemas, acaoInicial, onAbrir }) {
  const [acao, setAcao] = useState(acaoInicial || '');
  useEffect(() => { setAcao(acaoInicial || ''); }, [acaoInicial]);
  const itens = (problemas?.itens || []).filter((p) => !acao || p.acao === acao);
  const { ordenadas, Th } = useOrdenacao(itens, null);
  const max = Math.max(1, ...itens.flatMap((p) => Object.values(p.porDia || {})));
  const exportar = () => exportToExcel(`problemas-banco-${new Date().toISOString().slice(0, 10)}`, [
    { label: 'Gravidade', value: (p) => p.gravidade },
    { label: 'Problema', value: (p) => p.titulo },
    { label: 'Quem resolve', value: (p) => ACAO[p.acao]?.rotulo },
    { label: 'Quanto', value: (p) => `${p.numeroTexto} ${p.unidade}`.trim() },
    { label: 'Quando', value: (p) => quandoTexto(p.quando) },
    { label: 'Onde', value: (p) => p.onde.join(' · ') },
    { label: 'O que pedimos', value: (p) => p.pedido },
    { label: 'Técnico', value: (p) => p.tecnico },
  ], ordenadas, 'Problemas do banco');
  return (
    <>
      <div className="filter-card" style={{ marginBottom: 14 }}>
        <div className="filter-card__field">
          <label>Quem resolve</label>
          <div className="ab-chips">
            {[['', 'Todos'], ['micro', 'Microuniverso'], ['nos', 'Nós (TI)']].map(([k, r]) => (
              <button key={k || 'todos'} type="button" className={`vw-chip ${acao === k ? 'vw-chip--on' : 'vw-chip--off'}`} onClick={() => setAcao(k)}>{r}</button>
            ))}
          </div>
        </div>
        <div className="filter-card__field filter-card__field--grow" style={{ alignItems: 'flex-end' }}>
          <button type="button" className="btn-portal btn-portal--outline" onClick={exportar} style={{ marginLeft: 'auto' }}><i className="bi bi-file-earmark-excel" /> Excel</button>
        </div>
      </div>
      <div className="table-shell">
        <table className="portal-table">
          <thead>
            <tr>
              <Th campo="gravidade" valor={(p) => GRAV_ORDEM[p.gravidade]}>Gravidade</Th>
              <Th campo="titulo">Problema</Th>
              <Th campo="acao">Quem resolve</Th>
              <th style={{ textAlign: 'right' }}>Quanto</th>
              <th>Quando</th>
              <th style={{ width: 170 }}>Últimos 90 dias</th>
            </tr>
          </thead>
          <tbody>
            {ordenadas.map((p) => (
              <tr key={p.id} onClick={() => onAbrir(p)} style={{ cursor: 'pointer' }}>
                <td><Gravidade g={p.gravidade} /></td>
                <td>
                  <span className="cell-strong">{p.titulo}</span><br />
                  <small className="cell-muted">{TIPO[p.tipo]} · {p.onde.join(' · ')}</small>
                </td>
                <td><QuemResolve acao={p.acao} /></td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}><span className="cell-strong">{p.numeroTexto}</span> <small className="cell-muted">{p.unidade}</small></td>
                <td className="cell-muted">{quandoTexto(p.quando, { curto: true })}</td>
                <td>{p.porDia ? <Faixa90 porDia={p.porDia} max={max} cor={ORIGEM[p.dono]?.cor} /> : <small className="cell-muted">sem horário (coleta de 15 min)</small>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Pagina de um problema ───────────────────────────────────────────────────
function Copiar({ texto }) {
  const [ok, setOk] = useState(false);
  return (
    <button type="button" className="btn-portal btn-portal--outline" onClick={async () => {
      try { await navigator.clipboard.writeText(texto); setOk(true); setTimeout(() => setOk(false), 2500); } catch (e) { setOk(false); }
    }}>
      <i className={`bi ${ok ? 'bi-check2' : 'bi-clipboard'}`} /> {ok ? 'Copiado' : 'Copiar o pedido'}
    </button>
  );
}

export function ProblemaDetalhe({ id, onVoltar, onAbrirConsulta, onRelatorio }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [deadlockAberto, setDeadlockAberto] = useState(null);
  useEffect(() => {
    let valido = true;
    setDados(null); setErro('');
    axios.get(`${API}/analise-banco/problemas/${encodeURIComponent(id)}`)
      .then(({ data }) => { if (valido) setDados(data.data); })
      .catch((e) => { if (valido) setErro(e.response?.data?.message || 'Não foi possível abrir o problema.'); });
    return () => { valido = false; };
  }, [id]);

  if (erro) return <div className="feedback-banner feedback-banner--error">{erro}</div>;
  if (!dados) return <Carregando texto="Montando o problema (lendo o plano no servidor, quando é uma consulta)..." />;
  const p = dados.problema;
  const textoPedido = [
    `${p.titulo}`,
    `Impacto: ${p.numeroTexto} ${p.unidade}`.trim(),
    `Quando: ${quandoTexto(p.quando)}`,
    `Onde: ${p.onde.join(' · ')} (${p.tecnico})`,
    `O que acontece: ${p.oque}`,
    `O que pedimos: ${p.acao === 'ambos' ? p.lados?.micro : p.pedido}`,
  ].join('\n');

  return (
    <div className="ab-problema-pg">
      <div className="ab-problema-pg__topo" style={{ '--cor': ORIGEM[p.dono]?.cor }}>
        <div className="ab-problema-pg__selos"><Gravidade g={p.gravidade} /><PillOrigem origem={p.dono} /></div>
        <h2>{p.titulo}</h2>
        <p className="ab-problema-pg__linha">
          <strong>{p.numeroTexto} {p.unidade}</strong> · {quandoTexto(p.quando)}
        </p>
        <div className="ab-problema-pg__acoes">
          <span className="ab-problema-pg__quem">Quem resolve: <QuemResolve acao={p.acao} /></span>
          <Copiar texto={textoPedido} />
          {p.acao !== 'nos' && <button type="button" className="btn-portal btn-portal--outline" onClick={() => onRelatorio('micro')}><i className="bi bi-file-earmark-text" /> Abrir no dossiê da Micro</button>}
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-6">
          <section className="content-card ab-bloco">
            <h3 className="ab-bloco__h">O que acontece</h3>
            <p>{p.oque}</p>
            <h3 className="ab-bloco__h">O que pedimos</h3>
            {p.acao === 'ambos' ? (
              <ul className="ab-lados">
                <li><QuemResolve acao="micro" /> {p.lados?.micro}</li>
                <li><QuemResolve acao="nos" /> {p.lados?.nos}</li>
              </ul>
            ) : <p style={{ whiteSpace: 'pre-line' }}>{p.pedido.replace(/\n- /g, '\n• ')}</p>}
            <h3 className="ab-bloco__h">Onde</h3>
            <p>{p.onde.join(' · ')}<br /><code className="cell-muted">{p.tecnico}</code></p>
          </section>
        </div>
        <div className="col-12 col-xl-6">
          {p.porDia ? (
            <section className="content-card ab-bloco">
              <h3 className="ab-bloco__h">Quando</h3>
              <BarrasDia total={p.porDia} corDestaque={ORIGEM[p.dono]?.cor} />
              <GradeDiaHora celulas={p.porHora} />
            </section>
          ) : dados.porDia?.length ? (
            <section className="content-card ab-bloco">
              <h3 className="ab-bloco__h">Quando</h3>
              <BarrasDia total={Object.fromEntries(dados.porDia.map((d) => [diaIso(d.dia), Math.round(Number(d.cpu_ms) / 1000)]))} rotuloSerie="s de CPU" corDestaque={ORIGEM[p.dono]?.cor} />
            </section>
          ) : (
            <section className="content-card ab-bloco">
              <h3 className="ab-bloco__h">Quando</h3>
              <p className="cell-muted">{quandoTexto(p.quando)}. O horário de cada execução das consultas do ERP começa a existir com a coleta a cada 15 minutos.</p>
            </section>
          )}
        </div>
      </div>

      {dados.diagnostico && (
        <section className="content-card ab-bloco" style={{ marginTop: 14 }}>
          <div className="ab-bloco__topo">
            <h3>Evidência: o plano de execução</h3>
            <button type="button" className="ab-link" onClick={() => onAbrirConsulta(p.consultaId)}>abrir a consulta completa <i className="bi bi-arrow-right" /></button>
          </div>
          <Diagnostico id={p.consultaId} />
        </section>
      )}

      {dados.deadlocks && (
        <section className="content-card ab-bloco" style={{ marginTop: 14 }}>
          <div className="ab-bloco__topo">
            <h3>Evidência: os deadlocks</h3>
            <span className="cell-muted">{dados.totalDeadlocks} no total{dados.totalDeadlocks > dados.deadlocks.length ? ` · mostrando os ${dados.deadlocks.length} mais recentes` : ''} · clique para ver quem travou quem</span>
          </div>
          <div className="table-shell">
            <table className="portal-table">
              <thead><tr><th>Quando</th><th>O que aconteceu</th></tr></thead>
              <tbody>
                {dados.deadlocks.map((d) => (
                  <tr key={d.id} onClick={() => setDeadlockAberto(d.id)} style={{ cursor: 'pointer' }}>
                    <td style={{ whiteSpace: 'nowrap' }}>{dataHora(d.ocorrido_em)}</td>
                    <td className="ab-quebra">{d.narrativa}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <button type="button" className="ab-link" style={{ marginTop: 14 }} onClick={onVoltar}><i className="bi bi-arrow-left" /> voltar</button>
      {deadlockAberto && <ModalDeadlock id={deadlockAberto} onClose={() => setDeadlockAberto(null)} />}
    </div>
  );
}
